import React, {useContext, useEffect, useMemo, useState} from "react";
import {AiPromptServiceContext, AppContextContext, OrganizationServiceContext} from "../../Contexts";
import {Alert, Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Space, Spin, Table, Tag, Tooltip} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiPromptDetail.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import AiPrompt, {AiPromptUsage} from "../../domain/AiPrompt";
import {useForm} from "antd/es/form/Form";
import {AI_PROMPT_CONFIG_KEY_LABELS} from "./AiPromptConfigKeys";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import Organization from "../../domain/Organization";
import QueryOptions from "../../sal-ui/QueryOptions";
import {Permission} from "../../domain/auth/Permission";
import MarkdownContent from "../common/MarkdownContent";
import {AiPromptRequest} from "../../service/AiPromptService";
import AiPromptEditor from "./AiPromptEditor";

type AiPromptUsageRow = AiPromptUsage | {kind: "GLOBAL_SETTING"; id: string; name: string};

const USAGE_KIND_LABELS: Record<AiPromptUsageRow["kind"], string> = {
    GLOBAL_SETTING: "Global setting",
    AI_EXTRACTOR: "AI extractor",
    AI_REPORT_SUMMARIZER: "AI report summarizer"
};

function AiPromptDetail() {
    const appContext = useContext(AppContextContext);
    const aiPromptService = useContext(AiPromptServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const navigate = useNavigate();
    const {aiPromptId, clonePromptId} = useParams<{aiPromptId: string; clonePromptId: string}>();
    const [aiPrompt, setAiPrompt] = useState<AiPrompt>();
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [form] = useForm<AiPromptRequest>();
    const owner = Form.useWatch("owner", form);
    const isSystemAdmin = appContext.user.isSystemAdmin();
    const organizationId = appContext.user.organization?.id;
    const hasManage = isSystemAdmin || appContext.user.hasPermission(Permission.AI_PROMPT__MANAGE_ORG);
    const canManagePrompt = hasManage && (!aiPromptId || !!aiPrompt?.manageable);
    const title = clonePromptId ? `Clone of ${aiPrompt?.name ?? "AI prompt"}` : aiPromptId ? (aiPrompt?.name ?? "AI prompt") : "New AI prompt";
    const layout = {labelCol: {span: 4}, wrapperCol: {span: 20}};
    const tailLayout = {wrapperCol: {offset: 4, span: 20}};
    const usageRows = useMemo<AiPromptUsageRow[]>(() => [
        ...(aiPrompt?.configKeys ?? []).map(configKey => ({
            kind: "GLOBAL_SETTING" as const,
            id: configKey,
            name: AI_PROMPT_CONFIG_KEY_LABELS[configKey] || configKey
        })),
        ...(aiPrompt?.usage ?? [])
    ], [aiPrompt]);

    useEffect(() => {
        if (isSystemAdmin) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(result => setOrganizations(result.data));
        }
    }, [isSystemAdmin, organizationService]);

    useEffect(() => {
        let active = true;
        const sourceId = aiPromptId ?? clonePromptId;
        const defaults = {
            owner: isSystemAdmin ? ResourceOwnerType.SYSTEM : ResourceOwnerType.ORGANIZATION,
            organization: isSystemAdmin ? undefined : organizationId
        };

        setAiPrompt(undefined);
        setEditMode(!aiPromptId);
        setLoading(!!sourceId);

        form.resetFields();
        form.setFieldsValue(defaults);

        if (sourceId) {
            aiPromptService.get(sourceId).then(value => {
                if (!active) {
                    return;
                }

                setAiPrompt(value);

                form.setFieldsValue({
                    name: clonePromptId ? `Clone of ${value.name}`.slice(0, 100) : value.name,
                    description: value.description,
                    prompt: value.prompt,
                    owner: clonePromptId && !isSystemAdmin ? defaults.owner : value.owner,
                    organization: clonePromptId && !isSystemAdmin ? defaults.organization : value.organization?.id
                });
            }).finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        }

        return () => {
            active = false;
        };
    }, [aiPromptId, clonePromptId, aiPromptService, form, isSystemAdmin, organizationId]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]} items={[
                    {title: appContext.config?.appName},
                    {title: <Link to="/definitions/ai-prompts">AI prompts</Link>},
                    {title}
                ]}/>

                <Spin spinning={loading} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{title}</h1>

                    {aiPromptId && aiPrompt && hasManage &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            {canManagePrompt && <>
                                <Button disabled={saving} onClick={() => editMode ? cancelEdit() : setEditMode(true)}>Edit</Button>
                                <Tooltip title={aiPrompt.inUse ? "This prompt is in use and cannot be deleted." : undefined}>
                                    <span className={styles.deleteAction}><Button danger disabled={aiPrompt.inUse || saving} onClick={() => {
                                        Modal.confirm({
                                            content: <>Do you really want to delete AI prompt <b>{aiPrompt.name}</b>?</>,
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: onDelete
                                        });
                                    }}>Delete</Button></span>
                                </Tooltip>
                            </>}
                            <Button disabled={saving} onClick={() => navigate(`/definitions/ai-prompts/clone/${aiPromptId}`)}>Clone</Button>
                        </div>}

                    <Collapse isOpen={canManagePrompt && editMode}>
                        {aiPromptId && <h3>Edit AI prompt</h3>}
                        {aiPromptId && aiPrompt?.inUse &&
                            <Alert className={styles.notice} type="info" showIcon message="Changes to this prompt affect future runs of every configuration using it."/>}
                        <Form {...layout} form={form} onFinish={onFinish} disabled={saving}
                              className={`${globalStyles["common__form"]} ${aiPromptId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}>
                            <Form.Item name="name" label="Name" rules={[{required: true, whitespace: true, message: "Name is required."}]}>
                                <Input autoFocus maxLength={100}/>
                            </Form.Item>
                            <Form.Item name="description" label="Description" extra="Describe when to use this prompt. Markdown is supported.">
                                <Input.TextArea maxLength={1000} autoSize={{minRows: 3, maxRows: 8}}/>
                            </Form.Item>
                            {isSystemAdmin && <>
                                <Form.Item name="owner" label="Owner" rules={[{required: true, message: "Owner is required."}]}>
                                    <Select disabled={saving || !!aiPromptId} options={[
                                        {value: ResourceOwnerType.SYSTEM, label: "system"},
                                        {value: ResourceOwnerType.ORGANIZATION, label: "organization"}
                                    ]}/>
                                </Form.Item>
                                {owner === ResourceOwnerType.ORGANIZATION &&
                                    <Form.Item name="organization" label="Organization" rules={[{required: true, message: "Organization is required."}]}>
                                        <Select allowClear showSearch optionFilterProp="label" disabled={saving || !!aiPromptId}
                                                options={organizations?.map(organization => ({value: organization.id, label: organization.name}))}/>
                                    </Form.Item>}
                            </>}
                            <Form.Item name="prompt" label="Prompt" rules={[{required: true, whitespace: true, message: "Prompt is required."}]}>
                                <AiPromptEditor disabled={saving}/>
                            </Form.Item>
                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
                                <Button onClick={cancelEdit}>Cancel</Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {aiPromptId && aiPrompt && <>
                        <Descriptions column={1} bordered size="small" className={globalStyles.details} title="Prompt properties">
                            <Descriptions.Item label="Name">{aiPrompt.name}</Descriptions.Item>
                            <Descriptions.Item label="Owner">{aiPrompt.owner === ResourceOwnerType.SYSTEM ? <i>System</i> : aiPrompt.organization?.name}</Descriptions.Item>
                            <Descriptions.Item label="Description">{aiPrompt.description ? <MarkdownContent children={aiPrompt.description}/> : <i>no description</i>}</Descriptions.Item>
                            <Descriptions.Item label="Prompt"><div className={styles.prompt}>{aiPrompt.prompt}</div></Descriptions.Item>
                        </Descriptions>

                        <h2>Usage</h2>
                        {aiPrompt.inUse && <p>This prompt is in use and cannot be deleted. Only usages you have permission to read are listed.</p>}
                        {aiPrompt.usageTruncated && <Alert className={styles.notice} type="info" showIcon message="Only the first 100 visible usages are listed."/>}
                        <Table<AiPromptUsageRow>
                            dataSource={usageRows}
                            rowKey={usage => `${usage.kind}:${usage.id}`}
                            size="middle"
                            pagination={{pageSize: 10, hideOnSinglePage: true}}
                            locale={{emptyText: "No visible usage of this prompt."}}
                            columns={[
                                {title: "Type", dataIndex: "kind", render: (kind: AiPromptUsageRow["kind"]) => USAGE_KIND_LABELS[kind]},
                                {
                                    title: "Name",
                                    dataIndex: "name",
                                    render: (name, usage) => {
                                        if (usage.kind === "GLOBAL_SETTING") {
                                            return isSystemAdmin ? <Link to="/administration/ai/defaults">{name}</Link> : name;
                                        }

                                        const path = usage.kind === "AI_EXTRACTOR" ? "ai-extractors" : "ai-report-summarizers";

                                        return <Link to={`/definitions/${path}/${usage.id}`}>{name}</Link>;
                                    }
                                },
                                {
                                    title: "Role",
                                    render: (_, usage) => usage.kind === "GLOBAL_SETTING"
                                        ? <Tag>Default prompt</Tag>
                                        : <Space>
                                            {usage.systemPrompt && <Tag>Main prompt</Tag>}
                                            {usage.supplementaryPrompt && <Tag>Additional instructions</Tag>}
                                        </Space>
                                }
                            ]}
                        />
                    </>}
                </Spin>
            </>
        </DocumentTitle>
    );

    function cancelEdit() {
        if (!aiPromptId) {
            navigate("/definitions/ai-prompts");

            return;
        }

        setEditMode(false);

        form.setFieldsValue({
            name: aiPrompt.name,
            description: aiPrompt.description,
            prompt: aiPrompt.prompt,
            owner: aiPrompt.owner,
            organization: aiPrompt.organization?.id
        });
    }

    function onFinish(values: AiPromptRequest) {
        setSaving(true);

        const save = aiPromptId
            ? aiPromptService.update(aiPromptId, values).then(() => aiPromptService.get(aiPromptId)).then(value => {
                setAiPrompt(value);
                setEditMode(false);

                message.success("AI prompt updated.");
            })
            : aiPromptService.add(values).then(id => {
                message.success("AI prompt added.");

                navigate(`/definitions/ai-prompts/${id}`);
            });

        save.finally(() => setSaving(false));
    }

    function onDelete() {
        return aiPromptService.delete(aiPrompt).then(() => {
            message.success("AI prompt deleted.");

            navigate("/definitions/ai-prompts");
        });
    }
}

export default AiPromptDetail;
